import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { LeadFeedback } from "@/components/LeadFeedback";
import { postLead } from "@/lib/api";

const COUNTRY_OPTIONS = ["Not sure yet", "United Kingdom", "USA", "Canada", "Australia", "Germany", "New Zealand"];
const INTAKE_OPTIONS = ["Sep 2026", "Jan 2027", "May 2027", "Not decided yet"];
const MODE_OPTIONS = ["At our office", "Online"];

const PERKS = ["Profile & academic evaluation", "Country + university shortlist", "Loan & scholarship roadmap"];

const inputCls =
  "w-full rounded-2xl border border-white/10 bg-ink px-4 py-3.5 text-sm text-frost placeholder:text-mist/60 outline-none transition-all duration-300 focus:border-mint/60 focus:ring-2 focus:ring-mint/20";

export default function Booking() {
  const [searchParams] = useSearchParams();
  const requestedCountry = searchParams.get("country");
  const country = COUNTRY_OPTIONS.includes(requestedCountry) ? requestedCountry : "Not sure yet";
  const level = ["undergraduate", "postgraduate"].includes(searchParams.get("level")) ? searchParams.get("level") : "";
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country,
    intake: "Not decided yet",
    mode: "At our office",
    notes: country !== "Not sure yet" && level ? `Interested in ${level} study in ${country}.` : "",
  });
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setFeedback(null);
    try {
      await postLead("/bookings", form);
      setFeedback({ type: "success", message: "Thank you! Your counseling request has been saved. Your session time is not yet confirmed." });
      setForm((f) => ({ ...f, name: "", email: "", phone: "", notes: "" }));
    } catch (err) {
      setFeedback({ type: "error", message: err.message || "Could not book. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="border-y border-white/10 bg-surface/40">
      <div className="container-x grid grid-cols-1 items-center gap-14 py-20 lg:grid-cols-2 lg:gap-16 lg:py-32">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="overline-label flex items-center gap-3">
            <span className="h-px w-8 bg-mint" />
            Book free counseling
          </p>
          <h2 className="mt-5 font-display text-3xl font-light leading-[1.08] tracking-tight sm:text-4xl lg:text-5xl">
            Plan your entire study-abroad route in{" "}
            <span className="italic text-mint">30 minutes.</span>
          </h2>
          <ul className="mt-8 space-y-3.5">
            {PERKS.map((p) => (
              <li key={p} className="flex items-center gap-3 text-[15px] text-frost/90">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-mint/15 text-mint">
                  <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                {p}
              </li>
            ))}
          </ul>
          <div className="mt-10 overflow-hidden rounded-[28px] border border-white/10">
            <img
              src="https://images.unsplash.com/photo-1627556704290-2b1f5853ff78?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000&auto=format"
              alt="Graduates celebrating on campus"
              loading="lazy"
              className="h-64 w-full object-cover transition-transform duration-1000 hover:scale-[1.04] sm:h-72"
              data-testid="booking-image"
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-[28px] border border-white/10 bg-elevated/60 p-7 backdrop-blur-sm sm:p-9"
        >
          <form onSubmit={submit} data-testid="booking-counseling-form" className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="bk-name" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-mist">Full name</label>
                <input id="bk-name" required maxLength={120} autoComplete="name" value={form.name} onChange={set("name")} placeholder="Aarav Gupta" className={inputCls} data-testid="booking-name-input" />
              </div>
              <div>
                <label htmlFor="bk-phone" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-mist">Phone</label>
                <input id="bk-phone" type="tel" required maxLength={30} autoComplete="tel" value={form.phone} onChange={set("phone")} placeholder="Your phone with country code" className={inputCls} data-testid="booking-phone-input" />
              </div>
            </div>
            <div>
              <label htmlFor="bk-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-mist">Email</label>
              <input id="bk-email" type="email" required autoComplete="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} data-testid="booking-email-input" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="bk-country" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-mist">Preferred country</label>
                <select id="bk-country" value={form.country} onChange={set("country")} className={`${inputCls} appearance-none`} data-testid="booking-country-select">
                  {COUNTRY_OPTIONS.map((c) => <option key={c} value={c} className="bg-surface">{c}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="bk-intake" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-mist">Preferred intake</label>
                <select id="bk-intake" value={form.intake} onChange={set("intake")} className={`${inputCls} appearance-none`} data-testid="booking-intake-select">
                  {INTAKE_OPTIONS.map((c) => <option key={c} value={c} className="bg-surface">{c}</option>)}
                </select>
              </div>
            </div>
            <div>
              <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-mist">Consultation mode</span>
              <div className="grid grid-cols-2 gap-3">
                {MODE_OPTIONS.map((m) => (
                  <button
                    type="button"
                    aria-pressed={form.mode === m}
                    key={m}
                    onClick={() => setForm((f) => ({ ...f, mode: m }))}
                    data-testid={`booking-mode-${m === "Online" ? "online" : "office"}`}
                    className={`rounded-2xl border px-4 py-3 text-sm font-semibold transition-all duration-300 ${
                      form.mode === m
                        ? "border-mint bg-mint/10 text-mint"
                        : "border-white/10 bg-ink text-mist hover:border-mint/40"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label htmlFor="bk-notes" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-mist">
                Anything we should know? <span className="normal-case text-mist/50">(optional)</span>
              </label>
              <textarea id="bk-notes" rows={3} maxLength={1000} value={form.notes} onChange={set("notes")} placeholder="Course, budget, questions…" className={`${inputCls} resize-none`} data-testid="booking-notes-input" />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="booking-form-submit-btn"
              className="group flex w-full items-center justify-center gap-2 rounded-full bg-mint py-4 text-sm font-bold text-ink transition-all duration-300 hover:bg-mintlight hover:shadow-[0_0_44px_rgba(74,222,128,0.35)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Sending your request…</>
              ) : (
                <>Book my free session</>
              )}
            </button>
            <LeadFeedback feedback={feedback} form="booking" />
            <p className="text-center text-xs text-mist/70">No fees. No spam. Just a plan.</p>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
